// What the selected thing is made of.
//
// One panel per kind of object, holding the fields the Lua side panels hold —
// scene (properties_panel), picture (background_panel), words (text_panel),
// sound (audio_panel), tap area (trigger_areas_panel) — so an author who
// knows the app editor finds the same controls under the same names.
import React, { useMemo, useState } from "react"
import { twMerge } from "tailwind-merge"
import { ArrowDownTrayIcon, ArrowUpTrayIcon, TrashIcon } from "@heroicons/react/24/outline"
import Button from "aldoo-ra/Button"
import AudioPlayer from "aldoo-ra/AudioPlayer"
import useProductEditorTexts from "aldoo-ra/CMSModels/StoreProduct/useProductEditorTexts"
import { Field, NumberInput, Select, TextArea, TextInput } from "../../../AdminShared/ui"
import { Switch } from "../shared/shared"
import { urljoin } from "../shared/useProductEditor"
import {
  ALIGNMENTS,
  ANIM_FAST,
  ANIM_SLOW,
  FONT_SIZES,
  REVEAL_EFFECTS,
  TEXT_COLORS,
  TRANSITIONS,
  idOf,
  layerLayout,
  layerOf,
  transformOf,
} from "./editorModel"

const Group = ({ title, children, actions }) => (
  <section className="flex flex-col gap-2 border-b border-admin_bordered/60 pb-3 mb-3 last:border-0">
    <header className="flex items-center gap-2">
      <h4 className="m-0 text-xs font-semibold uppercase tracking-wide text-admin_text_secondary">{title}</h4>
      <span className="flex-1" />
      {actions}
    </header>
    {children}
  </section>
)

const Tiny = ({ children, onClick, title, active, disabled }) => (
  <button
    type="button"
    onClick={onClick}
    title={title}
    disabled={disabled}
    className={twMerge(
      "h-6 px-2 rounded text-[11px] border transition-colors",
      active ? "bg-admin_primary text-white border-admin_primary" : "border-admin_bordered text-admin_text_secondary hover:text-admin_text hover:bg-admin_neutral_1",
      disabled && "opacity-40 cursor-not-allowed"
    )}
  >
    {children}
  </button>
)

const Swatches = ({ colors, value, onPick }) => (
  <div className="flex items-center gap-1.5">
    {colors.map((color) => (
      <button
        key={color}
        type="button"
        onClick={() => onPick(color)}
        title={color}
        className={twMerge("w-6 h-6 rounded border", value === color ? "border-admin_primary ring-1 ring-admin_primary" : "border-admin_bordered")}
        style={{ background: color }}
      />
    ))}
    <TextInput value={value || ""} onChange={onPick} className="h-7 flex-1 text-xs" placeholder="#000000" />
  </div>
)

/** A picture or a sound on a content key: what it is now, and the ways to change it. */
const MediaField = ({ label, id, value, url, type, durations, onPick, onUpload, onClear, onRecord }) => {
  const t = useProductEditorTexts()
  return (
  <Field label={label}>
    <div className="flex items-center gap-2">
      {type === "image" ? (
        <span className="w-[64px] h-[40px] rounded bg-admin_neutral_1 overflow-hidden shrink-0 border border-admin_bordered">
          {url && <img src={url} alt="" className="w-full h-full object-cover" />}
        </span>
      ) : null}
      <div className="min-w-0 flex-1">
        <div className="text-xs truncate text-admin_text" title={value || ""}>
          {value ? value.split("/").pop() : <span className="italic text-admin_text_secondary">{t`nothing set`}</span>}
        </div>
        {type === "audio" && url && <AudioPlayer src={url} compact inlineTime showRate={false} className="mt-1" />}
        {type === "audio" && durations?.[value] ? (
          <div className="text-[11px] text-admin_text_secondary tabular-nums">{Math.round(durations[value])}s</div>
        ) : null}
      </div>
      <div className="flex flex-col gap-1">
        <Tiny onClick={() => onPick(type, id)} title={t`Pick from the media library`} disabled={!id}>
          {t`Library`}
        </Tiny>
        <Tiny onClick={() => onUpload(type, id)} title={t`Upload a file`} disabled={!id}>
          {t`Upload`}
        </Tiny>
        {onRecord ? (
          <Tiny onClick={onRecord} title={t`Read it aloud and record`} disabled={!id}>
            {t`Record`}
          </Tiny>
        ) : null}
        {value ? (
          <Tiny onClick={() => onClear(id)} title={t`Clear`}>
            {t`Clear`}
          </Tiny>
        ) : null}
      </div>
    </div>
  </Field>
  )
}

/** The box a thing occupies, in the 1000-wide design space the player draws in. */
const BoxFields = ({ box = {}, onChange }) => {
  const t = useProductEditorTexts()
  return (
  <div className="grid grid-cols-4 gap-2">
    {["left", "top", "width", "height"].map((key) => (
      <Field key={key} label={t(key)}>
        <NumberInput value={box[key] ?? 0} onChange={(value) => onChange(key, value)} className="h-8 text-xs" />
      </Field>
    ))}
  </div>
  )
}

export default function Inspector({
  editor,
  ops,
  selection,
  onSelect,
  layout,
  pageIndex,
  durations,
  onPickMedia,
  onUploadMedia,
  onRecord,
  phase,
  onPhase,
}) {
  const t = useProductEditorTexts()
  const { product, set, text, setText, language, defaultLanguage, mediaRoot } = editor
  const pages = product?.pages || []
  const page = pages[pageIndex]
  const [applying, setApplying] = useState(null)

  const value = (id) => (id ? text(id, language) || "" : "")
  const url = (id) => {
    const found = value(id) || (id ? text(id, defaultLanguage) : "")
    if (!found) return ""
    return /^https?:/i.test(found) ? found : urljoin(mediaRoot, found)
  }

  // the layer a layout actually reads, and the one written to
  const background = page?.background?.[layout] || layerOf(page?.background, layout) || {}
  const backgroundLayout = layerLayout(page?.background, layout)
  const subtitle = page?.subtitle?.[layout] || layerOf(page?.subtitle, layout) || {}
  const subtitleLayout = layerLayout(page?.subtitle, layout)

  const setPage = (path, next) => set(`pages.${pageIndex}.${path}`, next)
  const setBackground = (key, next) => setPage(`background.${backgroundLayout}.${key}`, next)
  const setSubtitle = (key, next) => setPage(`subtitle.${subtitleLayout}.${key}`, next)
  const clear = (id) => setText(id, "", language)

  const kind = selection?.kind || "scene"

  const fields = useMemo(
    () => ({
      background: { startTransform: "Start keyframe", endTransform: "End keyframe", duration: "Move length", transition: "Transition" },
      text: { renderBox: "Render box", textFrame: "Frame", font: "Font", fontSize: "Font size", alignment: "Alignment", color: "Colour" },
    }),
    []
  )

  /** The template button of the Lua panels: hand these fields to every other scene. */
  const applyAll = (group) => {
    const chosen = applying?.fields || []
    if (!chosen.length) return
    ops.applyToAllPages(pageIndex, group === "text" ? subtitleLayout : backgroundLayout, chosen, group)
    setApplying(null)
  }

  const TemplateButton = ({ group }) => (
    <Tiny onClick={() => setApplying(applying?.group === group ? null : { group, fields: Object.keys(fields[group]) })} active={applying?.group === group} title={t`Give these to every scene`}>
      {t`Template`}
    </Tiny>
  )

  const TemplatePicker = ({ group }) =>
    applying?.group === group ? (
      <div className="rounded-md border border-admin_bordered p-2 flex flex-col gap-1.5">
        <span className="text-[11px] text-admin_text_secondary">{t`Apply to every other scene:`}</span>
        {Object.entries(fields[group]).map(([key, label]) => (
          <label key={key} className="flex items-center gap-2 text-xs">
            <input
              type="checkbox"
              checked={applying.fields.includes(key)}
              onChange={(e) =>
                setApplying((current) => ({
                  ...current,
                  fields: e.target.checked ? [...current.fields, key] : current.fields.filter((name) => name !== key),
                }))
              }
            />
            {t(label)}
          </label>
        ))}
        <div className="flex gap-2 mt-1">
          <Button variant="admin_primary" className="h-[28px] text-xs px-3" onClick={() => applyAll(group)}>
            {t`Apply to all`}
          </Button>
          <Button variant="admin_secondary" className="h-[28px] text-xs px-3" onClick={() => setApplying(null)}>
            {t`Cancel`}
          </Button>
        </div>
      </div>
    ) : null

  if (!page) return <p className="text-sm text-admin_text_secondary italic">{t`No scene selected.`}</p>

  return (
    <div className="flex flex-col" data-testid="inspector">
      {/* which of the scene's parts is being edited */}
      <div className="flex gap-1 mb-3 flex-wrap">
        {[
          { kind: "scene", label: "Scene" },
          { kind: "background", label: "Picture" },
          { kind: "text", label: "Text" },
          { kind: "audio", label: "Sound" },
          { kind: "trigger", label: "Tap areas", count: page.triggerAreas?.length },
        ].map((tab) => (
          <button
            key={tab.kind}
            type="button"
            onClick={() => onSelect({ kind: tab.kind, order: tab.kind === "trigger" ? selection?.order ?? 0 : undefined })}
            className={twMerge(
              "h-7 px-2.5 rounded-md text-xs",
              kind === tab.kind ? "bg-admin_primary text-white" : "bg-admin_neutral_1 text-admin_text_secondary hover:text-admin_text"
            )}
            data-testid={`inspector-${tab.kind}`}
          >
            {t(tab.label)}
            {tab.count ? ` (${tab.count})` : ""}
          </button>
        ))}
      </div>

      {kind === "scene" && (
        <>
          <Group title={t("Scene %s of %s", pageIndex + 1, pages.length)}>
            <Field label={t("Title (%s)", language.toUpperCase())}>
              <TextInput value={value(idOf(page.title))} onChange={(next) => setText(idOf(page.title), next, language)} placeholder={t`Untitled scene`} />
            </Field>
            {product?.bookSettings?.useChapters && (
              <Field label={t`Chapter index`} hint={t`Which chapter this scene belongs to`}>
                <NumberInput value={page.chapterIndex ?? 0} onChange={(next) => setPage("chapterIndex", next)} />
              </Field>
            )}
            <Field label={t`Transition in`} hint={t`How this scene arrives (player_state.lua pageTransitions)`}>
              {/* older books carry transitions the app no longer offers ("Fade"):
                  the stored one stays in the list so opening the panel cannot
                  quietly change it */}
              <Select
                value={page.transition || TRANSITIONS[0]}
                onChange={(next) => setPage("transition", next)}
                options={[...new Set([...TRANSITIONS, page.transition].filter(Boolean))].map((name) => ({ label: t(name), value: name }))}
              />
            </Field>
          </Group>

          <Group title={t`Order`}>
            <div className="flex flex-wrap gap-1.5">
              <Tiny onClick={() => onSelect({ kind: "scene" }, ops.movePage(pageIndex, pageIndex - 1))} disabled={pageIndex === 0} title={t`Move up`}>
                {t`Move up`}
              </Tiny>
              <Tiny onClick={() => onSelect({ kind: "scene" }, ops.movePage(pageIndex, pageIndex + 1))} disabled={pageIndex >= pages.length - 1} title={t`Move down`}>
                {t`Move down`}
              </Tiny>
              <Tiny onClick={async () => onSelect({ kind: "scene" }, await ops.clonePage(pageIndex))} title={t`Copy this scene`}>
                {t`Duplicate`}
              </Tiny>
              <Tiny onClick={() => onSelect({ kind: "scene" }, ops.deletePage(pageIndex))} disabled={pages.length <= 1} title={t`Delete this scene`}>
                {t`Delete`}
              </Tiny>
            </div>
          </Group>
        </>
      )}

      {kind === "background" && (
        <>
          <Group title={t`Picture`} actions={<TemplateButton group="background" />}>
            <MediaField
              label={t("Image (%s)", t(backgroundLayout))}
              id={idOf(background.image)}
              value={value(idOf(background.image))}
              url={url(idOf(background.image))}
              type="image"
              onPick={onPickMedia}
              onUpload={onUploadMedia}
              onClear={clear}
            />
            <TemplatePicker group="background" />
          </Group>

          <Group
            title={t`Keyframes`}
            actions={
              <div className="flex gap-1">
                {["start", "end"].map((name) => (
                  <Tiny key={name} active={phase === name} onClick={() => onPhase(name)} title={t("Edit the %s keyframe", t(name))}>
                    {t(name)}
                  </Tiny>
                ))}
              </div>
            }
          >
            {["start", "end"].map((name) => {
              const transform = transformOf(background, name)
              const write = (key, next) =>
                setBackground(`${name}Transform`, {
                  ...transform,
                  ...(key === "x" || key === "y" ? { offsetPos: { ...transform.offsetPos, [key]: next } } : { [key]: next }),
                })
              return (
                <div key={name} className={twMerge("rounded-md border p-2", phase === name ? "border-admin_primary" : "border-admin_bordered")}>
                  <div className="text-[11px] text-admin_text_secondary mb-1 capitalize">{t(name)}</div>
                  <div className="grid grid-cols-4 gap-2">
                    <Field label={t`x`}>
                      <NumberInput value={transform.offsetPos?.x ?? 0} onChange={(next) => write("x", next)} className="h-8 text-xs" />
                    </Field>
                    <Field label={t`y`}>
                      <NumberInput value={transform.offsetPos?.y ?? 0} onChange={(next) => write("y", next)} className="h-8 text-xs" />
                    </Field>
                    <Field label={t`scale`}>
                      <NumberInput value={transform.scale ?? 1} step="0.01" onChange={(next) => write("scale", next)} className="h-8 text-xs" />
                    </Field>
                    <Field label={t`angle`}>
                      <NumberInput value={transform.angle ?? 0} onChange={(next) => write("angle", next)} className="h-8 text-xs" />
                    </Field>
                  </div>
                </div>
              )
            })}

            <div className="flex flex-wrap gap-1.5">
              <Tiny onClick={() => ops.copyPhase(pageIndex, backgroundLayout, "start", "end")} title={t`Start keyframe onto the end one`}>
                {t`start → end`}
              </Tiny>
              <Tiny onClick={() => ops.copyPhase(pageIndex, backgroundLayout, "end", "start")} title={t`End keyframe onto the start one`}>
                {t`end → start`}
              </Tiny>
              <Tiny onClick={() => ops.copyPhaseFromPage(pageIndex, pageIndex - 1, backgroundLayout, "end", "start")} disabled={pageIndex === 0} title={t`Continue from where the previous scene ended`}>
                {t`from previous`}
              </Tiny>
              <Tiny onClick={() => ops.copyPhaseFromPage(pageIndex, pageIndex + 1, backgroundLayout, "start", "end")} disabled={pageIndex >= pages.length - 1} title={t`End where the next scene starts`}>
                {t`from next`}
              </Tiny>
            </div>
          </Group>

          <Group title={t`Movement`}>
            <Field label={t`Length of the move (seconds)`} hint={t`0 leaves the picture still`}>
              <NumberInput value={background.duration ?? 0} onChange={(next) => setBackground("duration", next)} />
            </Field>
            <div className="flex gap-1.5">
              <Tiny onClick={() => setBackground("duration", ANIM_FAST)} active={background.duration === ANIM_FAST}>
                {t("fast (%ss)", ANIM_FAST)}
              </Tiny>
              <Tiny onClick={() => setBackground("duration", ANIM_SLOW)} active={background.duration === ANIM_SLOW}>
                {t("slow (%ss)", ANIM_SLOW)}
              </Tiny>
            </div>
            <Field label={t`Vertical focus`} hint={t`Where a taller screen crops to. Empty means the middle.`}>
              <div className="flex items-center gap-2">
                <input
                  type="range"
                  min={0}
                  max={1}
                  step={0.01}
                  value={background.verticalFocus ?? 0.5}
                  onChange={(e) => setBackground("verticalFocus", Number(e.target.value))}
                  className="flex-1"
                />
                <Tiny onClick={() => setBackground("verticalFocus", undefined)} disabled={background.verticalFocus === undefined}>
                  {t`clear`}
                </Tiny>
              </div>
            </Field>
          </Group>
        </>
      )}

      {kind === "text" && (
        <>
          <Group title={t`Words`} actions={<TemplateButton group="text" />}>
            <Field label={t("Text (%s)", language.toUpperCase())}>
              <TextArea value={value(idOf(subtitle.text))} onChange={(next) => setText(idOf(subtitle.text), next, language)} rows={4} placeholder={t`What is on screen`} />
            </Field>
            <MediaField
              label={t`Voice-over`}
              id={idOf(subtitle.audio)}
              value={value(idOf(subtitle.audio))}
              url={url(idOf(subtitle.audio))}
              type="audio"
              durations={durations}
              onPick={onPickMedia}
              onUpload={onUploadMedia}
              onClear={clear}
              onRecord={onRecord}
            />
            <TemplatePicker group="text" />
          </Group>

          <Group title={t("Render box (%s)", t(subtitleLayout))}>
            <BoxFields box={subtitle.renderBox} onChange={(key, next) => setSubtitle(`renderBox.${key}`, next)} />
          </Group>

          <Group title={t`Look`}>
            <Field label={t`Font`}>
              <TextInput value={subtitle.font || ""} onChange={(next) => setSubtitle("font", next)} placeholder={product?.defaultFont || t`the book's font`} />
            </Field>
            <div className="grid grid-cols-2 gap-2">
              <Field label={t`Font size`}>
                <NumberInput value={subtitle.fontSize ?? 18} onChange={(next) => setSubtitle("fontSize", next)} />
              </Field>
              <Field label={t`Alignment`}>
                <Select value={subtitle.alignment || "Left"} onChange={(next) => setSubtitle("alignment", next)} options={ALIGNMENTS.map((name) => ({ label: t(name), value: name }))} />
              </Field>
            </div>
            <div className="flex gap-1.5 flex-wrap">
              {FONT_SIZES.map((size) => (
                <Tiny key={size} onClick={() => setSubtitle("fontSize", size)} active={subtitle.fontSize === size}>
                  {size}
                </Tiny>
              ))}
            </div>
            <Field label={t`Colour`}>
              <Swatches colors={TEXT_COLORS} value={subtitle.color} onPick={(next) => setSubtitle("color", next)} />
            </Field>
            <Switch checked={subtitle.textFrame !== "normal"} onChange={(on) => setSubtitle("textFrame", on ? "comics" : "normal")} label={t`Comic frame behind the words`} />
            <Field label={t`Reveal`}>
              <Select value={subtitle.revealEffect || REVEAL_EFFECTS[0]} onChange={(next) => setSubtitle("revealEffect", next)} options={REVEAL_EFFECTS.map((name) => ({ label: t(name), value: name }))} />
            </Field>
          </Group>

          <Group title={t`Timing`}>
            <div className="grid grid-cols-2 gap-2">
              <Field label={t`Starts at (s)`} hint={t`After the scene begins`}>
                <NumberInput value={subtitle.startAt ?? 0} onChange={(next) => setSubtitle("startAt", next)} />
              </Field>
              <Field label={t`Shown for (s)`} hint={t`0 means to the end of the scene`}>
                <NumberInput value={subtitle.duration ?? 0} onChange={(next) => setSubtitle("duration", next)} />
              </Field>
            </div>
            <div className="flex gap-1.5 flex-wrap">
              <Tiny onClick={() => ops.copyTextStyle(pageIndex, pageIndex - 1, subtitleLayout)} disabled={pageIndex === 0}>
                {t`style from previous`}
              </Tiny>
              <Tiny onClick={() => ops.copyTextStyle(pageIndex, pageIndex + 1, subtitleLayout)} disabled={pageIndex >= pages.length - 1}>
                {t`style from next`}
              </Tiny>
            </div>
          </Group>
        </>
      )}

      {kind === "audio" && (
        <>
          <Group title={t`Sound effect`}>
            <MediaField
              label={t`Effect`}
              id={idOf(page.sfx)}
              value={value(idOf(page.sfx))}
              url={url(idOf(page.sfx))}
              type="audio"
              durations={durations}
              onPick={onPickMedia}
              onUpload={onUploadMedia}
              onClear={clear}
            />
            <Switch checked={!!page.sfxLoop} onChange={(on) => setPage("sfxLoop", on)} label={t`Loop until the scene ends`} />
            <Switch checked={!!page.usePrevSfx} onChange={(on) => setPage("usePrevSfx", on)} label={t`Keep the previous scene's effect playing`} />
          </Group>

          <Group title={t`Music`}>
            <MediaField
              label={t`Track`}
              id={idOf(page.music)}
              value={value(idOf(page.music))}
              url={url(idOf(page.music))}
              type="audio"
              durations={durations}
              onPick={onPickMedia}
              onUpload={onUploadMedia}
              onClear={clear}
            />
            <Switch checked={!!page.musicLoop} onChange={(on) => setPage("musicLoop", on)} label={t`Loop until the scene ends`} />
            <Switch checked={!!page.usePrevMusic} onChange={(on) => setPage("usePrevMusic", on)} label={t`Keep the previous scene's music playing`} />
          </Group>
        </>
      )}

      {kind === "trigger" && (
        <Group
          title={t("Tap areas %s", page.triggerAreas?.length ? `${(selection?.order ?? 0) + 1}/${page.triggerAreas.length}` : "0/0")}
          actions={
            <div className="flex gap-1">
              <Tiny onClick={async () => onSelect({ kind: "trigger", order: await ops.addTrigger(pageIndex) })} title={t`Add a tap area`}>
                {t`Add`}
              </Tiny>
              <Tiny
                onClick={() => {
                  ops.deleteTrigger(pageIndex, selection?.order ?? 0)
                  onSelect({ kind: "trigger", order: Math.max(0, (selection?.order ?? 0) - 1) })
                }}
                disabled={!page.triggerAreas?.length}
                title={t`Delete this tap area`}
              >
                {t`Delete`}
              </Tiny>
            </div>
          }
        >
          {!page.triggerAreas?.length && <p className="text-sm text-admin_text_secondary italic my-0">{t`This scene has no tap areas.`}</p>}

          {!!page.triggerAreas?.length && (
            <>
              <div className="flex gap-1 flex-wrap">
                {page.triggerAreas.map((_, order) => (
                  <Tiny key={order} active={(selection?.order ?? 0) === order} onClick={() => onSelect({ kind: "trigger", order })}>
                    {order + 1}
                  </Tiny>
                ))}
              </div>
              {(() => {
                const order = Math.min(selection?.order ?? 0, page.triggerAreas.length - 1)
                const trigger = page.triggerAreas[order] || {}
                const write = (key, next) => setPage(`triggerAreas.${order}.area.${key}`, next)
                return (
                  <>
                    <BoxFields box={trigger.area} onChange={write} />
                    <MediaField
                      label={t`Sound when tapped`}
                      id={idOf(trigger.audio)}
                      value={value(idOf(trigger.audio))}
                      url={url(idOf(trigger.audio))}
                      type="audio"
                      durations={durations}
                      onPick={onPickMedia}
                      onUpload={onUploadMedia}
                      onClear={clear}
                    />
                    <Field label={t`Go to scene`} hint={t`Empty for none. Scene numbers start at 1.`}>
                      <NumberInput value={trigger.gotoPage ?? ""} allowEmpty onChange={(next) => setPage(`triggerAreas.${order}.gotoPage`, next ?? undefined)} />
                    </Field>
                  </>
                )
              })()}
            </>
          )}
        </Group>
      )}
    </div>
  )
}
